// Lightweight accessors over the native Android `window.AndroidBridge` interface.
//
// This module intentionally imports NOTHING heavy (no store, no push handler) so
// it's safe to use from anywhere — including GenericUtility / permission helpers
// — without creating import cycles. The bridge *installer* (nativeBridge.ts) is
// the heavy module.

import { NativeNotificationState } from "../../interfaces/NotificationObject";

declare global {
  interface Window {
    AndroidBridge?: {
      onWebAppReady?: () => void;
      getFcmToken?: () => string;
      requestNotificationPermission?: () => void;
      isNotificationPermissionGranted?: () => boolean;
      getNotificationPermissionState?: () => string;
      openAppSettings?: () => void;
      // Downloads `url` into the public Downloads directory as `filename`, with the
      // system's own DownloadManager notification on completion. Used by the gallery
      // "download" fallback in DownloadHelper.ts, since there's no in-WebView download
      // manager to catch a browser-style blob download. Fire-and-forget — there's no
      // return value, DownloadManager's own notification is the completion signal.
      downloadImage?: (url: string, filename: string) => void;
    };
  }
}

export function callOnWebAppReady(): void {
  try {
    window.AndroidBridge?.onWebAppReady?.();
  } catch (error) {
    console.warn("AndroidBridge.onWebAppReady failed:", error);
  }
}

export function getNativeFcmToken(): string | null {
  try {
    return window.AndroidBridge?.getFcmToken?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * Single point of truth for collapsing an arbitrary bridge string to a
 * NativeNotificationState. Used by both the getter below and the push-callback
 * path (onPermissionChanged) in nativeBridge.ts.
 */
export function toNativeNotificationState(
  raw: string | undefined
): NativeNotificationState {
  return raw === "granted" || raw === "denied" || raw === "blocked"
    ? raw
    : "unknown";
}

/** Current native notification permission, with back-compat fallback. */
export function getNotificationPermissionState(): NativeNotificationState {
  try {
    const state = toNativeNotificationState(
      window.AndroidBridge?.getNotificationPermissionState?.()
    );
    if (state !== "unknown") return state;
    if (window.AndroidBridge?.isNotificationPermissionGranted?.()) {
      return "granted";
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/** Never dead-ends: native shows the OS dialog if it can, else opens Settings. */
export function requestAndroidNativeNotificationPermission(): void {
  try {
    window.AndroidBridge?.requestNotificationPermission?.();
  } catch (error) {
    console.warn("AndroidBridge.requestNotificationPermission failed:", error);
  }
}

/**
 * Opens this app's system Settings page. Used by the camera/location recovery
 * paths (QrScannerModal, permissionRecovery). NOT used for the notification
 * "Open settings" CTA — there, requestNotificationPermission() already opens
 * Settings itself when the permission is blocked (per the native contract).
 */
export function openAppSettings(): void {
  try {
    window.AndroidBridge?.openAppSettings?.();
  } catch (error) {
    console.warn("AndroidBridge.openAppSettings failed:", error);
  }
}

/** True when this build of the native app exposes the downloadImage bridge method. */
export function androidCanDownloadImage(): boolean {
  return typeof window.AndroidBridge?.downloadImage === "function";
}

/**
 * Asks native to download `url` into the device's public Downloads directory as
 * `filename`. Fire-and-forget: native shows its own completion notification, there is
 * nothing to await here.
 */
export function downloadImageAndroid(url: string, filename: string): void {
  try {
    window.AndroidBridge?.downloadImage?.(url, filename);
  } catch (error) {
    console.warn("AndroidBridge.downloadImage failed:", error);
  }
}
